import type { VFC } from 'react';
import { Col, Row } from 'antd';
import { RowGutter } from 'common/spacing';
import { useLicenses, useLicensesAnalysis } from 'api/hooks/customer';
import type { ResellersTab } from 'routes/resellers/enums/resellersTab';
import { Granularity, Period, SplitBy } from '@enmon/commons/lib/enmon';
import { LicenseChart } from 'components/chart/License';
import { useRecoilState } from 'recoil';
import { ResellersTable } from './components/Table';
import { Statistics } from './components/Statistics';
import { ResellersFilter } from './components/Filter';
import { resellersFilter } from './store/filter';

const licenseChartFilter = {
  granularity: Granularity.Month,
  period: Period.Last24months,
};

export const Overview: VFC<{ id: ResellersTab }> = () => {
  const [filter, setFilter] = useRecoilState(resellersFilter);

  const { data: resellersResponse, isFetching: isLoadingResellers } = useLicenses({
    split: SplitBy.Resellers,
    ...filter,
  });
  const { result: resellers, count: resellersCount } = resellersResponse ?? { result: [], count: 0 };

  const { data: statsResponse, isFetching: isLoadingLicensesStatistics } = useLicenses(filter);
  const { result: licensesStatistics } = statsResponse ?? { result: [], count: 0 };
  const { customers, opex } = licensesStatistics?.[0]?.stats ?? {};

  const { data, isFetching } = useLicensesAnalysis({
    depth: -1,
    ...licenseChartFilter,
    period: filter.period,
    ...(filter.customer && { customers: [filter.customer] }),
  });

  return (
    <Row gutter={[RowGutter.Default, RowGutter.Default]}>
      <Col span={24}>
        <ResellersFilter />
      </Col>
      <Col span={24}>
        <Statistics isLoading={isLoadingLicensesStatistics} {...opex} {...{ customers }} />
      </Col>
      <Col span={24}>
        <LicenseChart isFetching={isFetching} licenseAnalysisData={data} />
      </Col>
      <Col span={24}>
        <ResellersTable
          isLoading={isLoadingResellers}
          pagination={{
            total: resellersCount,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} Resellers`,
            pageSize: filter.pageSize,
            current: filter.page,
            onChange(page, pageSize) {
              setFilter({ ...filter, page, pageSize });
            },
          }}
          resellers={resellers}
        />
      </Col>
    </Row>
  );
};
